import PropTypes from 'prop-types';
import React, { memo } from 'react';
import Grid from '@material-ui/core/Grid';
import { lookupsJson } from '@ourbranch/lookups';
import { Form } from 'formik';

import CollapsibleCard from 'core/components/collapsible-card';
import { FormField } from 'core/components/form';
import { Label } from 'core/components/label';
import RemoveButton from 'core/components/remove-button';

import { useRouteHelper } from 'core/hooks/use-route-helper';
import {
  ENTITY_TYPE_OPTIONS,
  getDescriptionLabel,
  getVisibilityFlags
} from 'common/helpers/farm-interested-party-helpers';
import useStyles from '../../people/person.styles';

function FarmInterestedParty({
  locationOptions,
  equipmentOptions,
  riskOptions,
  sppOptions,
  rpsOptions,
  index,
  fieldName,
  item,
  removeFromList,
  onRemove,
  initialFold,
  onFold,
  disabled
}) {
  const classes = useStyles();
  const { inPolicyRoute } = useRouteHelper();
  const context = inPolicyRoute ? 'policy' : 'offer';
  const emptyMsg = (itemType) =>
    `There are no ${itemType} on this ${context}. Please add one if you want to associate it to the interested party.`;
  const {
    showLocationFields,
    showEquipmentFields,
    showLocationAndRiskFields,
    showLocationRiskAndEquipmentFields,
    showDescriptionOnly,
    showAllFields
  } = getVisibilityFlags(item.relationship);

  return (
    <CollapsibleCard
      content={
        <Label
          type="infoSubtitle"
          style={{
            display: 'inline',
            marginRight: 8
          }}
        >{`${item.name}`}</Label>
      }
      contentRight={!item.isPrimary && <RemoveButton mode="big" onClick={() => onRemove(index, removeFromList)} />}
      initialOpen={initialFold}
      onChange={(val) => onFold(index, val)}
    >
      <Form disabled={disabled} className={classes.container}>
        <Grid key="container" container justifyContent="space-around" alignItems="center" spacing={4}>
          <FormField
            mode="light"
            name={`${fieldName}.name`}
            id={`${fieldName}.name`}
            label="Name or Company"
            type="string"
            xs={12}
            permissions={{ isLicensedAction: false }}
          />
          <FormField
            mode="light"
            name={`${fieldName}.address.address`}
            id={`${fieldName}.address.address`}
            label="Address"
            type="address"
            xs={8}
            permissions={{ isLicensedAction: false }}
          />
          <FormField
            mode="light"
            name={`${fieldName}.address.address2`}
            id={`${fieldName}.address.address2`}
            label="Address 2"
            type="string"
            optional
            xs={4}
            permissions={{ isLicensedAction: false }}
          />
          <FormField
            mode="light"
            name={`${fieldName}.address.city`}
            id={`${fieldName}.address.city`}
            label="City"
            type="string"
            optional
            xs={4}
            permissions={{ isLicensedAction: false }}
          />
          <FormField
            mode="light"
            name={`${fieldName}.address.state`}
            id={`${fieldName}.address.state`}
            label="State"
            type="state"
            xs={4}
            permissions={{ isLicensedAction: false }}
          />
          <FormField
            mode="light"
            name={`${fieldName}.address.zip`}
            id={`${fieldName}.address.zip`}
            label="Zip code"
            type="string"
            optional
            xs={4}
            permissions={{ isLicensedAction: false }}
          />
          <FormField
            mode="light"
            name={`${fieldName}.relationship`}
            id={`${fieldName}.relationship`}
            label="Relationship"
            type="select"
            options={lookupsJson.farm.farmInterestedPartyTypes}
            xs={12}
            permissions={{ isLicensedAction: false }}
          />
          {item.relationship === 'partnerOrCoOwner' && (
            <FormField
              mode="light"
              name={`${fieldName}.entityType`}
              id={`${fieldName}.entityType`}
              label="Entity Type"
              type="select"
              options={ENTITY_TYPE_OPTIONS}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          )}
          {showLocationFields && (
            <>
              <FormField
                mode="light"
                name={`${fieldName}.locations`}
                id={`${fieldName}.locations`}
                label="Locations"
                description="Select all that apply"
                type="chipArray"
                options={locationOptions}
                emptyMessage={emptyMsg('locations')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              {item.relationship === 'occupant' && (
                <FormField
                  mode="light"
                  name={`${fieldName}.isResident`}
                  id={`${fieldName}.isResident`}
                  label="Is this additional insured a resident of the household?"
                  type="checkbox"
                  xs={12}
                  permissions={{ isLicensedAction: false }}
                />
              )}
              <FormField
                mode="light"
                name={`${fieldName}.description`}
                id={`${fieldName}.description`}
                label={getDescriptionLabel(item.relationship)}
                type="textarea"
                minRows={3}
                maxRows={3}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
            </>
          )}
          {showEquipmentFields && (
            <>
              <FormField
                mode="light"
                name={`${fieldName}.equipment`}
                id={`${fieldName}.equipment`}
                label="Associated equipment"
                description="Select all that apply"
                type="chipArray"
                options={equipmentOptions}
                emptyMessage={emptyMsg('equipment')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.description`}
                id={`${fieldName}.description`}
                label="Describe any leased equipment not listed on the policy (if applicable)"
                type="textarea"
                minRows={3}
                maxRows={3}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
            </>
          )}
          {showLocationAndRiskFields && (
            <>
              <FormField
                mode="light"
                name={`${fieldName}.locations`}
                id={`${fieldName}.locations`}
                label="Associated locations/properties"
                description="Select all that apply"
                type="chipArray"
                options={locationOptions}
                emptyMessage={emptyMsg('locations')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.risks`}
                id={`${fieldName}.risks`}
                label="Associated risks"
                description="Select all that apply"
                type="chipArray"
                options={riskOptions}
                emptyMessage={emptyMsg('risks')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.description`}
                id={`${fieldName}.description`}
                label="Description"
                type="textarea"
                minRows={3}
                maxRows={3}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
            </>
          )}
          {showLocationRiskAndEquipmentFields && (
            <>
              <FormField
                mode="light"
                name={`${fieldName}.locations`}
                id={`${fieldName}.locations`}
                label="Associated locations/properties"
                description="Select all that apply"
                type="chipArray"
                options={locationOptions}
                emptyMessage={emptyMsg('locations')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.risks`}
                id={`${fieldName}.risks`}
                label="Associated risks"
                description="Select all that apply"
                type="chipArray"
                options={riskOptions}
                emptyMessage={emptyMsg('risks')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.equipment`}
                id={`${fieldName}.equipment`}
                label="Associated equipment"
                description="Select all that apply"
                type="chipArray"
                options={equipmentOptions}
                emptyMessage={emptyMsg('equipment')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.description`}
                id={`${fieldName}.description`}
                label="Description"
                type="textarea"
                minRows={3}
                maxRows={3}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
            </>
          )}
          {showDescriptionOnly && (
            <FormField
              mode="light"
              name={`${fieldName}.description`}
              id={`${fieldName}.description`}
              label="Description"
              type="textarea"
              minRows={3}
              maxRows={3}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          )}
          {showAllFields && (
            <>
              <FormField
                mode="light"
                name={`${fieldName}.locations`}
                id={`${fieldName}.locations`}
                label="Associated locations/properties"
                description="Select all that apply"
                type="chipArray"
                options={locationOptions}
                emptyMessage={emptyMsg('locations')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.risks`}
                id={`${fieldName}.risks`}
                label="Associated risks"
                description="Select all that apply"
                type="chipArray"
                options={riskOptions}
                emptyMessage={emptyMsg('risks')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.equipment`}
                id={`${fieldName}.equipment`}
                label="Associated equipment"
                description="Select all that apply"
                type="chipArray"
                options={equipmentOptions}
                emptyMessage={emptyMsg('equipment')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.sppItems`}
                id={`${fieldName}.sppItems`}
                label="Associated inland marine assets"
                description="Select all that apply"
                type="chipArray"
                options={sppOptions}
                emptyMessage={emptyMsg('inland marine assets')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.rpsItems`}
                id={`${fieldName}.rpsItems`}
                label="Associated related private structures"
                description="Select all that apply"
                type="chipArray"
                options={rpsOptions}
                emptyMessage={emptyMsg('related private structures')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name={`${fieldName}.description`}
                id={`${fieldName}.description`}
                label={getDescriptionLabel(item.relationship)}
                type="textarea"
                minRows={3}
                maxRows={3}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
            </>
          )}
        </Grid>
      </Form>
    </CollapsibleCard>
  );
}

FarmInterestedParty.propTypes = {
  locationOptions: PropTypes.array,
  equipmentOptions: PropTypes.array,
  riskOptions: PropTypes.array,
  sppOptions: PropTypes.array,
  rpsOptions: PropTypes.array,
  index: PropTypes.number.isRequired,
  fieldId: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  item: PropTypes.shape({
    isPrimary: PropTypes.bool,
    name: PropTypes.string.isRequired,
    relationship: PropTypes.string.isRequired,
    address: PropTypes.object
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  removeFromList: PropTypes.func.isRequired,
  initialFold: PropTypes.bool.isRequired,
  onFold: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

FarmInterestedParty.defaultProps = {
  disabled: false,
  locationOptions: [],
  equipmentOptions: [],
  riskOptions: [],
  sppOptions: [],
  rpsOptions: []
};

export default memo(FarmInterestedParty);
